import { BoxGeometry, InstancedMesh, MeshStandardMaterial, Object3D, Color } from 'three';
import { Rng } from '../../core/rng';
import { canvasTexture, repeating } from '../../render/materials/canvasTexture';

const COUNT = 90;
const SPAN = 420;
const TINTS = ['#e8e4dc', '#d9dee3', '#b9cad6', '#f0e6d2', '#c9d6cf', '#aab8c2'];

interface Tower {
  x: number;
  z: number;
  w: number;
  h: number;
  d: number;
}

function windowsTexture() {
  return repeating(
    canvasTexture(64, 64, (ctx) => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 64, 64);
      ctx.fillStyle = '#6f8594';
      for (let y = 6; y < 64; y += 16) ctx.fillRect(0, y, 64, 7);
    }),
    3,
    6,
  );
}

/** Distant towers and blocks on both sides, fading into the haze. They wrap around as you drive. */
export class Skyline {
  readonly mesh: InstancedMesh;
  private readonly towers: Tower[] = [];
  private readonly helper = new Object3D();

  constructor() {
    this.mesh = new InstancedMesh(new BoxGeometry(1, 1, 1), new MeshStandardMaterial({ map: windowsTexture() }), COUNT);
    this.mesh.frustumCulled = false;
    const rng = new Rng(2024);
    const color = new Color();
    for (let i = 0; i < COUNT; i += 1) {
      const side = i % 2 === 0 ? 1 : -1;
      const tall = rng.next() > 0.7;
      this.towers.push({
        x: side * rng.range(55, 190),
        z: -rng.range(0, SPAN),
        w: rng.range(12, 30),
        h: tall ? rng.range(35, 75) : rng.range(10, 28),
        d: rng.range(12, 26),
      });
      this.mesh.setColorAt(i, color.set(rng.pick(TINTS)));
    }
  }

  /** `urban` (0–1) is how built-up the country is here: out in the hills the towers sink away. */
  update(busZ: number, urban = 1): void {
    this.mesh.visible = urban > 0.05;
    if (!this.mesh.visible) return;
    this.towers.forEach((tower, i) => {
      // Keep each tower within [busZ - SPAN + 60, busZ + 60] by wrapping.
      const rel = ((((tower.z - busZ - 60) % SPAN) - SPAN) % SPAN) + 60;
      this.helper.position.set(tower.x, (tower.h * urban) / 2, busZ + rel);
      this.helper.scale.set(tower.w, tower.h * urban, tower.d);
      this.helper.updateMatrix();
      this.mesh.setMatrixAt(i, this.helper.matrix);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
